import { useMemo, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { DataGrid } from '@/components/tables/DataGrid';
import { AsyncButton } from '@/components/common/AsyncButton';
import { useToast } from '@/components/common/toast';
import { moneyTransactionsApi } from '@/api/services/moneyTransactions';
import type { ServerDataTable } from '@/components/tables/useServerDataTable';
import type {
  ProviderTransactionDto,
  StatementCommentDto,
  StatementViewModel,
} from '@/api/types/providerStatements';
import type { ProviderStatementConfig } from './config';
import { statementColumns, transactionColumns } from './providerStatementColumns';
import { CommentConfirmDialog } from '../providerFunds/CommentConfirmDialog';
import { BulkEditProviderDialog } from './BulkEditProviderDialog';
import { StatementCommentsPanel } from './StatementCommentsPanel';

interface Props {
  config: ProviderStatementConfig;
  transactions: ServerDataTable<ProviderTransactionDto>;
  statements: ServerDataTable<StatementViewModel>;
  can: {
    match: boolean;
    delete: boolean;
    editProvider: boolean;
  };
  onReloadAll: () => void;
}

type CommentPanel = { title: string; comments: StatementCommentDto[]; loading: boolean };

export function UnmatchedTab({ config, transactions, statements, can, onReloadAll }: Props) {
  const toast = useToast();
  const { api, family, statementIdField, title } = config;

  // Selection: single transaction (ticket + connectorId), many statement ids.
  const [selectedTx, setSelectedTx] = useState<{ ticket: number; connectorId: number } | null>(null);
  const [selectedStatementIds, setSelectedStatementIds] = useState<Set<string>>(new Set());
  // Bulk edit-provider checkbox selection, keyed "ticket|connectorId".
  const [checked, setChecked] = useState<Set<string>>(new Set());

  const [matchDialog, setMatchDialog] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<string | null>(null);
  const [editProviderOpen, setEditProviderOpen] = useState(false);
  const [panel, setPanel] = useState<CommentPanel | null>(null);

  const statementId = (row: StatementViewModel): string =>
    String((row as unknown as Record<string, unknown>)[statementIdField] ?? '');

  // --- Mutations --------------------------------------------------------------------
  const matchMutation = useMutation({
    mutationFn: (comment: string) =>
      api.matchTransactions({
        statementIds: Array.from(selectedStatementIds),
        ticket: selectedTx!.ticket,
        connectorId: selectedTx!.connectorId,
        comment,
      }),
    onSuccess: (res) => {
      toast.success(res.message);
      setMatchDialog(false);
      clearSelection();
      onReloadAll();
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (comment: string) => api.deleteStatement({ statementId: deleteTarget!, comment }),
    onSuccess: () => {
      toast.success('Statement deleted successfully');
      setDeleteTarget(null);
      onReloadAll();
    },
  });

  const bulkEditMutation = useMutation({
    mutationFn: (args: { provider: string; comment: string }) =>
      moneyTransactionsApi.bulkUpdateProvider({
        provider: args.provider,
        comment: args.comment,
        transactionsToUpdate: Array.from(checked).map((k) => {
          const [ticket, dataSourceId] = k.split('|');
          return { ticket: Number(ticket), dataSourceId: Number(dataSourceId) };
        }),
      }),
    onSuccess: (res) => {
      const successCount = res.success?.length ?? 0;
      const failed = res.failed ?? [];
      if (successCount > 0 && failed.length === 0) {
        toast.success('All requests submitted for approval.');
      } else if (successCount > 0 && failed.length > 0) {
        toast.warning(`${successCount} submitted, ${failed.length} failed.`);
      } else {
        toast.error(`No requests were submitted. ${failed.map((f) => f.note ?? 'Unknown error').join('; ')}`);
      }
      setEditProviderOpen(false);
      setChecked(new Set());
      onReloadAll();
    },
  });

  // --- Comment panel loaders --------------------------------------------------------
  const txCommentsMutation = useMutation({
    mutationFn: (ticket: number) => api.getUnmatchedTransactionsComments(ticket),
  });
  const stCommentsMutation = useMutation({
    mutationFn: (id: string) => api.getUnmatchedStatementsComments(id),
  });

  function openTxComments(ticket: number) {
    setPanel({ title: 'Audit Trail', comments: [], loading: true });
    txCommentsMutation.mutate(ticket, {
      onSuccess: (items) => setPanel({ title: 'Audit Trail', comments: items, loading: false }),
    });
  }
  function openStatementComments(id: string) {
    setPanel({ title: 'Audit Trail', comments: [], loading: true });
    stCommentsMutation.mutate(id, {
      onSuccess: (items) => setPanel({ title: 'Audit Trail', comments: items, loading: false }),
    });
  }

  function clearSelection() {
    setSelectedTx(null);
    setSelectedStatementIds(new Set());
  }

  const matchEnabled = Boolean(selectedTx) && selectedStatementIds.size > 0;

  // --- Transactions grid ------------------------------------------------------------
  const txSearchValues = useMemo(() => transactions.columnFilters, [transactions.columnFilters]);
  const txKey = (r: ProviderTransactionDto) => `${r.ticket}|${r.connectorId}`;

  const allChecked =
    transactions.rows.length > 0 && transactions.rows.every((r) => checked.has(txKey(r)));

  function toggleCheckAll(value: boolean) {
    setChecked((prev) => {
      const next = new Set(prev);
      transactions.rows.forEach((r) => {
        if (value) next.add(txKey(r));
        else next.delete(txKey(r));
      });
      return next;
    });
  }

  function toggleCheck(r: ProviderTransactionDto, value: boolean) {
    setChecked((prev) => {
      const next = new Set(prev);
      if (value) next.add(txKey(r));
      else next.delete(txKey(r));
      return next;
    });
  }

  const txRowActions = (r: ProviderTransactionDto) => (
    <div className="d-flex align-items-center gap-1">
      {can.editProvider && (
        <input
          type="checkbox"
          className="form-check-input mt-0"
          checked={checked.has(txKey(r))}
          onChange={(e) => toggleCheck(r, e.target.checked)}
          onClick={(e) => e.stopPropagation()}
        />
      )}
      <button
        type="button"
        className="btn btn-sm btn-link p-0 px-1"
        title="Audit trail"
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          openTxComments(r.ticket);
        }}
      >
        <i className="fa fa-plus-circle" />
      </button>
    </div>
  );

  const stRowActions = (row: StatementViewModel) => {
    const id = statementId(row);
    return (
      <div className="d-flex align-items-center gap-1">
        <button
          type="button"
          className="btn btn-sm btn-link p-0 px-1"
          title="Audit trail"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            openStatementComments(id);
          }}
        >
          <i className="fa fa-plus-circle" />
        </button>
        {can.delete && (
          <button
            type="button"
            className="btn btn-sm btn-link text-danger p-0 px-1"
            title="Delete statement"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              setDeleteTarget(id);
            }}
          >
            <i className="fa fa-trash" />
          </button>
        )}
      </div>
    );
  };

  const txHeaderCheckbox = can.editProvider ? (
    <input
      type="checkbox"
      className="form-check-input mt-0"
      checked={allChecked}
      onChange={(e) => toggleCheckAll(e.target.checked)}
      title="Select all"
    />
  ) : null;

  function toggleStatement(row: StatementViewModel) {
    const id = statementId(row);
    setSelectedStatementIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  return (
    <div>
      <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-2">
        <p className="mb-0 text-muted small">
          Select one platform transaction and one or more statement rows, then Match.
        </p>
        <div className="d-flex align-items-center gap-2">
          {can.editProvider && (
            <AsyncButton
              className="btn btn-outline-primary btn-sm"
              icon="fa fa-edit"
              disabled={checked.size === 0}
              onClick={() => setEditProviderOpen(true)}
            >
              Edit Provider
            </AsyncButton>
          )}
          {can.match && (
            <AsyncButton
              className="btn btn-primary btn-sm"
              disabled={!matchEnabled}
              onClick={() => setMatchDialog(true)}
            >
              Match
            </AsyncButton>
          )}
        </div>
      </div>

      <div className="row g-3">
        <div className="col-12 col-lg-6">
          <h6 className="text-center">Unmatched Provider transactions captured from trading platform</h6>
          <DataGrid
            columns={transactionColumns}
            data={transactions.rows}
            rowKey={txKey}
            columnSearch
            columnSearchValues={txSearchValues}
            onColumnSearchChange={transactions.setColumnFilter}
            includeExcel
            excelFileName={`${title} Unmatched Transactions`}
            rowActions={txRowActions}
            toolbarStart={txHeaderCheckbox}
            onRowClick={(r) =>
              setSelectedTx((cur) =>
                cur && cur.ticket === r.ticket && cur.connectorId === r.connectorId
                  ? null
                  : { ticket: r.ticket, connectorId: r.connectorId },
              )
            }
            rowClassName={(r) =>
              selectedTx && selectedTx.ticket === r.ticket && selectedTx.connectorId === r.connectorId
                ? 'ps-selected table-active'
                : undefined
            }
            server={{
              pageIndex: transactions.pageIndex,
              pageSize: transactions.pageSize,
              pageCount: transactions.pageCount,
              totalCount: transactions.totalCount,
              onPageChange: transactions.setPageIndex,
              onPageSizeChange: transactions.setPageSize,
            }}
            emptyMessage={transactions.isFetching ? 'Loading…' : 'No data available'}
          />
        </div>

        <div className="col-12 col-lg-6">
          <h6 className="text-center">Unmatched Provider transactions captured from statement upload</h6>
          <DataGrid
            columns={statementColumns(family)}
            data={statements.rows}
            rowKey={(r) => statementId(r)}
            columnSearch
            columnSearchValues={statements.columnFilters}
            onColumnSearchChange={statements.setColumnFilter}
            includeExcel
            excelFileName={`${title} Unmatched Statements`}
            rowActions={stRowActions}
            onRowClick={toggleStatement}
            rowClassName={(r) => (selectedStatementIds.has(statementId(r)) ? 'ps-selected table-active' : undefined)}
            server={{
              pageIndex: statements.pageIndex,
              pageSize: statements.pageSize,
              pageCount: statements.pageCount,
              totalCount: statements.totalCount,
              onPageChange: statements.setPageIndex,
              onPageSizeChange: statements.setPageSize,
            }}
            emptyMessage={statements.isFetching ? 'Loading…' : 'No data available'}
          />
        </div>
      </div>

      {panel && (
        <StatementCommentsPanel
          title={panel.title}
          comments={panel.comments}
          loading={panel.loading}
          onClose={() => setPanel(null)}
        />
      )}

      <CommentConfirmDialog
        show={matchDialog}
        title="Are you sure?"
        body={
          <p className="mb-0">
            Match {Array.from(selectedStatementIds).join(', ')} with {selectedTx?.ticket}?
          </p>
        }
        confirmLabel="Yes"
        busy={matchMutation.isPending}
        onConfirm={(comment) => matchMutation.mutate(comment)}
        onCancel={() => setMatchDialog(false)}
      />

      <CommentConfirmDialog
        show={deleteTarget !== null}
        title="Are you sure?"
        body={<p className="mb-0">Delete statement {deleteTarget}?</p>}
        confirmLabel="Delete"
        confirmVariant="danger"
        busy={deleteMutation.isPending}
        onConfirm={(comment) => deleteMutation.mutate(comment)}
        onCancel={() => setDeleteTarget(null)}
      />

      <BulkEditProviderDialog
        show={editProviderOpen}
        currentProviderTitle={title}
        selectedCount={checked.size}
        busy={bulkEditMutation.isPending}
        onConfirm={(provider, comment) => bulkEditMutation.mutate({ provider, comment })}
        onCancel={() => setEditProviderOpen(false)}
      />
    </div>
  );
}
